import type { Dispatch, SetStateAction } from 'react';
import React, { useCallback, useMemo } from 'react';
import { List as DxList, ItemDragging } from 'devextreme-react/list';
import { CardHeader, NuvoCard } from '@nuvolo/nuux-core/components/NuvoCard';
import merge from 'deepmerge';
import { NuvoLink } from '@nuvolo/nuux-core/components/NuvoLink';
import { NuvoText } from '@nuvolo/nuux-core/components/NuvoText';
import { NuvoBox } from '@nuvolo/nuux-core/components/NuvoBox';
import { NuvoCheckbox } from '@nuvolo/nuux-core/components/NuvoCheckbox';
import { useViewport } from '@nuvolo/nuux-theme/hooks/useViewport';
import { useSwitch } from '@nuvolo/nuux-core/hooks/useSwitch';
import type { Item } from '../../hooks/useDashboardItems';
import Styles from './Styles';
import arrayMove from '#/common/utils/arrayMove';
import If from '#/common/components/If';
import styled from 'styled-components';
import { color } from '@nuvolo/nuux-theme/utils';
import { borderRadius } from '#/common/hooks/useClientTheme';
import FAIcon from '#/common/components/FAIcon';

const StyledConfigItem = styled.div<{ $isDesktop: boolean }>`
  > div > div {
    border-radius: 32px;
    overflow: hidden;
    border: 1px solid ${color('custom', 'dashboardConfigItemBorder')} !important;
  }

  > div > div > div {
    &:first-child {
      display: none;
    }
    padding: 20px 20px;

    > div:first-child > div:first-child {
      gap: ${({ $isDesktop }) => ($isDesktop ? '4px' : 'initial')};
    }
  }

  .fa-solid {
    width: 40px;
    height: 40px;
    background-color: ${color('success', 'light')};
    border-radius: 14px;
    opacity: 1;
    transition: color 0.2s ease;
    &::before {
      color: ${color('contrast', 'light')};
    }
  }

  h1 {
    font-size: 16px;
    font-weight: 500;
    transition: opacity 0.2s ease;
    color: ${color('custom', 'contrastText')};
    margin-left: ${({ $isDesktop }) => ($isDesktop ? '0px' : '10px')};
  }

  &[data-visible='true'] h1 {
    opacity: 1;
  }

  &[data-visible='false'] h1 {
    opacity: 0.4;
  }

  &[data-visible='false'] .fa-solid {
    background-color: ${color('ui', 'light2')};
  }

  .dashboard-config-divider {
    border-bottom: 1px solid ${color('primary', 'light3')};
    margin: 12px 0 24px 0;
    width: 100%;
  }
`;

export interface ConfigListProps {
  items: Item[];
  onChange: Dispatch<SetStateAction<Item[]>>;
}

const ConfigList: React.FC<ConfigListProps> = ({ items, onChange }) => {
  const { isDesktop } = useViewport();
  const { value: showVisible, set: setVisibility } = useSwitch(false);

  const renderItem = useCallback(
    (item: Item) => {
      const updateItem = (partItem: Partial<Item>) => {
        onChange((oldItems) => {
          const newItems = merge([], oldItems);
          const itemIndex = newItems.findIndex((it) => it.key === item.key);
          if (itemIndex >= 0) {
            newItems[itemIndex] = {
              ...newItems[itemIndex],
              ...partItem,
            };
          }
          return newItems;
        });
      };

      return (
        <StyledConfigItem data-visible={item.show} $isDesktop={isDesktop}>
          <NuvoCard status={item.show ? 'success' : 'warning'}>
            <CardHeader
              title={item.title}
              icon={{
                source: 'other' as const,
                icon: () => <FAIcon icon={item.show ? item.icon : 'eye-slash'} />,
              }}
              onClick={isDesktop ? undefined : () => updateItem({ show: !item.show })}>
              {isDesktop && <div className="dashboard-config-divider"> </div>}
              {isDesktop && (
                <NuvoBox display="flex" flexDirection="row" gap="15%">
                  <NuvoBox>
                    <NuvoText type="body" className="config-item-label">
                      Visibility
                    </NuvoText>
                    <NuvoLink type="body" onClick={() => updateItem({ show: !item.show })}>
                      {item.show ? 'Hide' : 'Show'}
                    </NuvoLink>
                  </NuvoBox>
                  <NuvoBox>
                    <NuvoText type="body" className="config-item-label">
                      Size
                    </NuvoText>
                    <NuvoLink
                      type="body"
                      inline
                      className={item.colSpan?.md === 3 ? 'extra-bold' : undefined}
                      onClick={() => updateItem({ colSpan: { xs: 12, md: 3, xl: 3 } })}>
                      Small
                    </NuvoLink>{' '}
                    |{' '}
                    <NuvoLink
                      type="body"
                      inline
                      className={item.colSpan?.md === 6 ? 'extra-bold' : undefined}
                      onClick={() => updateItem({ colSpan: { xs: 12, md: 6, xl: 6 } })}>
                      Medium
                    </NuvoLink>{' '}
                    |{' '}
                    <NuvoLink
                      type="body"
                      inline
                      className={item.colSpan?.md === 12 ? 'extra-bold' : undefined}
                      onClick={() => updateItem({ colSpan: { xs: 12, md: 12, xl: 12 } })}>
                      Large
                    </NuvoLink>
                  </NuvoBox>
                </NuvoBox>
              )}
            </CardHeader>
          </NuvoCard>
        </StyledConfigItem>
      );
    },
    [isDesktop, onChange],
  );

  const onReorder = useCallback(
    (e: { fromIndex: number; toIndex: number }) => {
      onChange((oldItems) => {
        const newItems = merge([], oldItems);
        arrayMove(newItems, e.fromIndex, e.toIndex);
        return newItems;
      });
    },
    [onChange],
  );

  const ds = useMemo(() => {
    if (showVisible) {
      return items.filter((it) => it.show);
    }
    return items;
  }, [items, showVisible]);

  return (
    <Styles>
      <NuvoBox display="flex" paddingY={4}>
        <NuvoCheckbox
          value={showVisible}
          onValueChange={setVisibility}
          text="Show only visible items"
        />
      </NuvoBox>
      <If condition={!isDesktop}>
        <NuvoBox display="flex" alignItems="center" gap={5} paddingTop={4}>
          <FAIcon icon="eye-slash" />
          <NuvoText type="body">Tap on an item to toggle its visibility.</NuvoText>
        </NuvoBox>
      </If>
      <DxList dataSource={ds} itemRender={renderItem} selectionMode="none">
        <ItemDragging allowReordering onReorder={onReorder} />
      </DxList>
    </Styles>
  );
};

export default React.memo(ConfigList);
